import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { uploadTargetListToS3 } from '../../utility/target-list-upload/s3-upload';
import { parseSegmentedFile } from '../../utility/target-list-upload/parse-segmented-list';
import { uploadSegmentedTargetLists } from '../../utility/target-list-upload/segmented-upload';
import {
  CreateTargetListMutation,
  CreateTargetListMutationVariables,
  CreateTargetListDocument,
  DuplicateClicksCreativeDocument,
  DuplicateClicksCreativeMutation,
  DuplicateClicksCreativeMutationVariables,
  DuplicateFmaCreativeDocument,
  DuplicateFmaCreativeMutation,
  DuplicateFmaCreativeMutationVariables,
  UpdateTargetListDocument,
  UpdateTargetListMutation,
  UpdateTargetListMutationVariables,
  UpdateFmaCreativeMutationVariables,
  UpdateFmaCreativeMutation,
  CreateClicksCreativeMutation,
  CreateClicksCreativeMutationVariables,
  UpdateClicksCreativeDocument,
  CreateClicksCreativeDocument,
  SetFmaCreativeTargetListsMutation,
  SetFmaCreativeTargetListsMutationVariables,
  SetFmaCreativeTargetListsDocument,
  CreateFmaCreativeMutationVariables,
  CreateFmaCreativeDocument,
  CreateFmaCreativeMutation,
  SetClicksCreativeListsMutation,
  SetClicksCreativeListsMutationVariables,
  SetClicksCreativeListsDocument,
  UpdateFmaCreativeDocument,
  UpdateClicksCreativeMutation,
  UpdateClicksCreativeMutationVariables,
  ClicksCreative,
  ArchiveLabelMutation,
  ArchiveLabelMutationVariables,
  ArchiveLabelDocument,
  ClicksCreativeTargetingType,
  CreateSponsoredContentTeaserMutation,
  CreateSponsoredContentTeaserMutationVariables,
  CreateSponsoredContentTeaserDocument,
  UpdateSponsoredContentTeaserDocument,
  SetSponsoredContentTeaserListsDocument,
  SponsoredContentTeaser,
  UpdateEndemicDriverDocument,
  UpdateEndemicDriverMutation,
  UpdateEndemicDriverMutationVariables,
  CreateEndemicDriverDocument,
  CreateEndemicDriverMutation,
  CreateEndemicDriverMutationVariables,
  SetEndemicDriverTargetListsDocument,
  SetEndemicDriverTargetListsMutation,
  SetEndemicDriverTargetListsMutationVariables,
  SetEndemicDriverTopicTagsDocument,
  SetEndemicDriverTopicTagsMutation,
  SetEndemicDriverTopicTagsMutationVariables,
  SetEndemicDriverSpecialtiesDocument,
  SetEndemicDriverSpecialtiesMutation,
  SetEndemicDriverSpecialtiesMutationVariables,
  SetEndemicDriverProfessionsDocument,
  SetEndemicDriverProfessionsMutation,
  SetEndemicDriverProfessionsMutationVariables,
  CreateLeadGenCampaignMutation,
  CreateLeadGenCampaignMutationVariables,
  CreateLeadGenCampaignDocument,
  UpdateLeadGenCampaignMutation,
  UpdateLeadGenCampaignMutationVariables,
  UpdateLeadGenCampaignDocument,
  SetLeadGenCampaignTargetListsDocument,
  GetLeadGenCampaignDocument,
  GetLeadGenCampaignQuery,
  GetLeadGenCampaignQueryVariables,
} from 'src/generated/client';
import { isNativeLineItemId } from '../../utility/pio-native-lineitem-fk-reads';

export interface CreateTargetListVariables extends CreateTargetListMutationVariables {
  targetListFile: { rawFile: File };
  npisList: number[];
  isSegmented?: boolean;
}

type NativeLineItemLookupResult = {
  lineItems?: {
    nodes: Array<{ id: string }>;
  } | null;
};

type PlacementsLineItemLinkedData = {
  placementsLineItemId?: string | number | null;
  lineItemId?: string | null;
};

type WithNativeLineItemIdForWriteOptions = {
  clearLineItemIdOnLookupMiss?: boolean;
  // MD-5145: only the four creative tables had placements_line_item_id relaxed to
  // NULLABLE (migration 000095). Resources whose placements_line_item_id is still
  // NOT NULL (e.g. line_item_labels, where it's also part of the primary key) must
  // KEEP the value — set this true for them so the native strip is skipped.
  keepPlacementsLineItemIdForNative?: boolean;
};

export const NATIVE_LINE_ITEM_ID_BY_EXTERNAL_PIO_ID_FOR_WRITE = gql`
  query NativeLineItemIdByExternalPioIdForWrite($externalPioId: String!) {
    lineItems(first: 1, filter: { externalPioId: { equalTo: $externalPioId } }) {
      nodes {
        id
      }
    }
  }
`;

const presentString = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return undefined;

  const stringValue = String(value);
  return stringValue.length > 0 ? stringValue : undefined;
};

export const resolveNativeLineItemIdForWrite = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  placementsLineItemId: string | number | null | undefined,
) => {
  const legacyLineItemId = presentString(placementsLineItemId);

  if (!legacyLineItemId) return undefined;
  if (isNativeLineItemId(legacyLineItemId)) return legacyLineItemId;

  const queryResult = await apolloClient.query<NativeLineItemLookupResult>({
    query: NATIVE_LINE_ITEM_ID_BY_EXTERNAL_PIO_ID_FOR_WRITE,
    variables: {
      externalPioId: legacyLineItemId,
    },
    fetchPolicy: 'network-only',
  });

  return queryResult.data.lineItems?.nodes[0]?.id;
};

export const withNativeLineItemIdForWrite = async <T extends PlacementsLineItemLinkedData>(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  data: T,
  options: WithNativeLineItemIdForWriteOptions = {},
): Promise<T> => {
  if (!presentString(data.placementsLineItemId)) return data;

  try {
    const nativeLineItemId = await resolveNativeLineItemIdForWrite(apolloClient, data.placementsLineItemId);

    if (nativeLineItemId) {
      // MD-5145: when the resolved line item is native (the placementsLineItemId
      // is itself the native UUID), the placements_line_item_id column is now
      // nullable (creative tables only) and must not hold the native UUID. Set it
      // to NULL explicitly — NOT omitted: a PostGraphile update patch leaves omitted
      // columns UNCHANGED, so omitting would keep a stale legacy id when an imported
      // creative is re-pointed at a native line item. Sending null clears it on
      // update and is equivalent to omit on create. Persist the native lineItemId FK.
      // Imported rows keep both fields. Resources whose placements_line_item_id is
      // still NOT NULL (line_item_labels) opt out via keepPlacementsLineItemIdForNative.
      if (isNativeLineItemId(data.placementsLineItemId) && !options.keepPlacementsLineItemIdForNative) {
        return {
          ...data,
          placementsLineItemId: null,
          lineItemId: nativeLineItemId,
        } as T;
      }

      return {
        ...data,
        lineItemId: nativeLineItemId,
      };
    }

    return options.clearLineItemIdOnLookupMiss ? { ...data, lineItemId: null } : data;
  } catch (error) {
    console.error('withNativeLineItemIdForWrite: native line item lookup failed', error);
    throw new Error('Could not resolve native line item ID for write');
  }
};

export const createTargetList = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  variables: CreateTargetListVariables,
  getToken: () => Promise<string | null>,
) => {
  // Handle segmented uploads
  if (variables.isSegmented) {
    // Parse the segmented file
    const parseResult = await parseSegmentedFile(variables.targetListFile.rawFile);

    // Check for parsing errors
    if (parseResult.errors.length > 0) {
      throw new Error(`Segmented file parsing failed: ${parseResult.errors.join(', ')}`);
    }

    // Check if any segments were found
    if (parseResult.segments.size === 0) {
      throw new Error('No valid segments found in file');
    }

    // Upload all segments
    const uploadResult = await uploadSegmentedTargetLists(
      apolloClient,
      variables.name,
      variables.description || '',
      parseResult.segments,
      getToken,
    );

    // Check for upload errors
    if (!uploadResult.success) {
      throw new Error(uploadResult.error || 'Failed to upload segmented target lists');
    }

    // Show notification about skipped NPIs if any
    if (parseResult.skippedCount > 0) {
      console.warn(`Skipped ${parseResult.skippedCount} NPIs with empty segment values`);
    }

    // Return summary data
    return {
      data: {
        id: null, // No single ID for segmented uploads
        name: variables.name,
        description: variables.description,
        npiCount: uploadResult.totalNpiCount,
        createdLists: uploadResult.createdLists,
        segmentCount: parseResult.segments.size,
        skippedCount: parseResult.skippedCount,
      },
    };
  }

  // Standard single list upload
  const createResult = await apolloClient.mutate<CreateTargetListMutation, CreateTargetListMutationVariables>({
    mutation: CreateTargetListDocument,
    variables: {
      description: variables.description,
      name: variables.name,
      uploadedFilename: variables.targetListFile.rawFile.name,
    },
  });

  const newTargetListId = createResult.data?.createTargetList?.targetList?.id;

  let uploadResponse;
  if (newTargetListId) {
    uploadResponse = await uploadTargetListToS3(variables.targetListFile.rawFile, newTargetListId, getToken);
  }
  return {
    data: {
      ...createResult?.data?.createTargetList?.targetList,
      npiCount: uploadResponse?.data?.uploadedCount ?? 0,
    },
  };
};

export interface UpdateTargetListVariables extends UpdateTargetListMutationVariables {
  targetListFile?: { rawFile: File };
  npisList?: number[];
  targetListLineItemIdsText?: string | string[] | null;
}

type SetTargetListLineItemsMutation = {
  setTargetListLineItems?: {
    query?: {
      targetList?: {
        id: number;
        name: string;
        description?: string | null;
        uploadedFilename: string;
        uploadedAt: string;
        npiCount?: number | null;
        placementLineItemIdsList?: Array<string | null> | null;
        lineItemIdsList?: Array<string | null> | null;
        isArchived: boolean;
      } | null;
    } | null;
  } | null;
};

type SetTargetListLineItemsMutationVariables = {
  targetListId: number;
  lineItemIds: string[];
};

const SetTargetListLineItemsDocument = gql`
  mutation SetTargetListLineItems($targetListId: Int!, $lineItemIds: [String]!) {
    setTargetListLineItems(input: { targetListId: $targetListId, lineItemIds: $lineItemIds }) {
      query {
        targetList(id: $targetListId) {
          id
          name
          description
          uploadedFilename
          uploadedAt
          npiCount
          placementLineItemIdsList
          lineItemIdsList
          isArchived
        }
      }
    }
  }
`;

export const parseTargetListLineItemIds = (value: unknown): string[] => {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(/[\s,]+/)
      : [];
  const seen = new Set<string>();

  return rawValues
    .map(rawValue => String(rawValue ?? '').trim())
    .filter(lineItemId => {
      if (!lineItemId || seen.has(lineItemId)) {
        return false;
      }

      seen.add(lineItemId);
      return true;
    });
};

export const updateTargetList = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  variables: UpdateTargetListVariables,
  getToken: () => Promise<string | null>,
) => {
  const shouldSetLineItems = Object.prototype.hasOwnProperty.call(variables, 'targetListLineItemIdsText');
  const lineItemIds = shouldSetLineItems ? parseTargetListLineItemIds(variables.targetListLineItemIdsText) : undefined;
  const input: UpdateTargetListMutationVariables = {
    id: variables.id,
    description: variables.description,
    name: variables.name,
    isArchived: variables.isArchived,
  };

  const fileToUpload = variables?.targetListFile?.rawFile;

  if (fileToUpload) {
    input.uploadedFilename = fileToUpload.name;
  }

  const updateResult = await apolloClient.mutate<UpdateTargetListMutation, UpdateTargetListMutationVariables>({
    mutation: UpdateTargetListDocument,
    variables: input,
  });

  let uploadResponse;
  if (fileToUpload) {
    uploadResponse = await uploadTargetListToS3(fileToUpload, input.id, getToken);
  }

  let targetList = updateResult?.data?.updateTargetList?.targetList;
  if (lineItemIds) {
    const lineItemsResult = await apolloClient.mutate<
      SetTargetListLineItemsMutation,
      SetTargetListLineItemsMutationVariables
    >({
      mutation: SetTargetListLineItemsDocument,
      variables: {
        targetListId: input.id,
        lineItemIds,
      },
    });

    targetList = lineItemsResult.data?.setTargetListLineItems?.query?.targetList ?? targetList;
  }

  return {
    data: {
      ...targetList,
      npiCount: uploadResponse?.data?.uploadedCount ?? targetList?.npiCount ?? 0,
    },
  };
};

export const duplicateClicksCreative = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  variables: DuplicateClicksCreativeMutationVariables,
) => {
  const duplicateMutationResult = await apolloClient.mutate<
    DuplicateClicksCreativeMutation,
    DuplicateClicksCreativeMutationVariables
  >({
    mutation: DuplicateClicksCreativeDocument,
    variables,
  });

  const duplicatedCreative = duplicateMutationResult?.data?.duplicateClicksCreative?.clicksCreative;

  return {
    data: duplicatedCreative,
    id: duplicatedCreative?.id,
  };
};

export const createFmaCreative = async (apolloClient: ApolloClient<NormalizedCacheObject>, variables: any) => {
  const { targetListIdsList, ...fmaCreative } = variables;
  const fmaCreativeWithLineItemId = await withNativeLineItemIdForWrite(apolloClient, fmaCreative);
  const createMutationResult = await apolloClient.mutate<CreateFmaCreativeMutation, CreateFmaCreativeMutationVariables>(
    {
      mutation: CreateFmaCreativeDocument,
      variables: {
        input: {
          fmaCreative: fmaCreativeWithLineItemId,
        },
      },
    },
  );

  const newFmaCreativeId = createMutationResult.data?.createFmaCreative?.fmaCreative?.id;

  if (newFmaCreativeId) {
    await apolloClient.mutate<SetFmaCreativeTargetListsMutation, SetFmaCreativeTargetListsMutationVariables>({
      mutation: SetFmaCreativeTargetListsDocument,
      variables: {
        input: {
          fmaCreativeId: newFmaCreativeId,
          targetListIds: targetListIdsList ?? [],
        },
      },
    });
  }

  return {
    data: createMutationResult?.data?.createFmaCreative?.fmaCreative,
  };
};

export const updateFmaCreative = async (apolloClient: ApolloClient<NormalizedCacheObject>, variables: any) => {
  const { id, targetListIdsList, __typename, ...patch } = variables;

  // hasActivityData is generated from a function and is readonly
  delete patch.hasActivityData;
  const patchWithLineItemId = await withNativeLineItemIdForWrite(apolloClient, patch, {
    clearLineItemIdOnLookupMiss: true,
  });

  const updateMutationResult = await apolloClient.mutate<UpdateFmaCreativeMutation, UpdateFmaCreativeMutationVariables>(
    {
      mutation: UpdateFmaCreativeDocument,
      variables: {
        id,
        patch: patchWithLineItemId,
        targetListIds: targetListIdsList,
      },
    },
  );

  return {
    data: {
      ...updateMutationResult?.data?.updateFmaCreative?.fmaCreative,
      placementsLineItemId: variables.placementsLineItemId,
    },
  };
};

export const duplicateFmaCreative = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  variables: DuplicateFmaCreativeMutationVariables,
) => {
  const duplicateMutationResult = await apolloClient.mutate<
    DuplicateFmaCreativeMutation,
    DuplicateFmaCreativeMutationVariables
  >({
    mutation: DuplicateFmaCreativeDocument,
    variables,
  });

  const duplicatedCreative = duplicateMutationResult?.data?.duplicateFmaCreative?.fmaCreative;

  return {
    data: duplicatedCreative,
    id: duplicatedCreative?.id,
  };
};

export const duplicateLeadGenCampaign = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  variables: { existingId: number },
) => {
  const existingResult = await apolloClient.query<GetLeadGenCampaignQuery, GetLeadGenCampaignQueryVariables>({
    query: GetLeadGenCampaignDocument,
    variables: { id: variables.existingId },
    fetchPolicy: 'network-only',
  });

  const existingCampaign = existingResult.data.leadGenCampaign;

  if (!existingCampaign) {
    throw new Error('Lead gen campaign not found');
  }

  const createResult = await apolloClient.mutate<CreateLeadGenCampaignMutation, CreateLeadGenCampaignMutationVariables>(
    {
      mutation: CreateLeadGenCampaignDocument,
      variables: {
        name: `Copy of ${existingCampaign.name}`,
        startsAt: existingCampaign.startsAt,
        endsAt: existingCampaign.endsAt,
        priority: existingCampaign.priority ?? 0,
        maxViewsPerNpi: existingCampaign.maxViewsPerNpi ?? 1,
        maxViewsPerSession: existingCampaign.maxViewsPerSession ?? 1,
        isApproved: false,
      },
    },
  );

  const newLeadGenCampaign = createResult.data?.createLeadGenCampaign?.leadGenCampaign;
  const newLeadGenCampaignId = newLeadGenCampaign?.id;

  if (newLeadGenCampaignId) {
    const targetListIds = (existingCampaign.targetListIdsList ?? []).filter(
      (targetListId): targetListId is number => targetListId != null,
    );

    if (targetListIds.length > 0) {
      await apolloClient.mutate({
        mutation: SetLeadGenCampaignTargetListsDocument,
        variables: {
          id: newLeadGenCampaignId,
          targetListIds,
        },
      });
    }
  }

  return {
    data: newLeadGenCampaign,
    id: newLeadGenCampaignId,
  };
};

type CsapiStructuredError = {
  message?: unknown;
  hint?: unknown;
};

export type CurrentLineItemReviewer = {
  id: string;
  name?: string | null;
  email?: string | null;
};

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

export const getCsapiStructuredErrorMessage = (responseBody: unknown, fallbackMessage: string) => {
  if (!responseBody || typeof responseBody !== 'object') {
    return fallbackMessage;
  }

  const errorBody = responseBody as CsapiStructuredError;
  if (isNonEmptyString(errorBody.message)) {
    return isNonEmptyString(errorBody.hint) ? `${errorBody.message} ${errorBody.hint}` : errorBody.message;
  }

  return fallbackMessage;
};

export const getDuplicateLineItemErrorMessage = (responseBody: unknown) => {
  return getCsapiStructuredErrorMessage(responseBody, 'Could not duplicate line item');
};

export const getCurrentLineItemReviewerErrorMessage = (responseBody: unknown) => {
  return getCsapiStructuredErrorMessage(responseBody, 'Could not resolve current user as reviewer');
};

const getCsapiBaseUrl = () => {
  const baseUrl = process.env.NEXT_PUBLIC_CSAPI_BASE_URL;

  if (!baseUrl) {
    throw new Error('CSAPI base URL is not configured');
  }

  return baseUrl;
};

const getBearerAuthHeaders = async (getToken: () => Promise<string | null>) => {
  const authToken = await getToken();
  return authToken ? { Authorization: `Bearer ${authToken}` } : undefined;
};

export const resolveCurrentLineItemReviewer = async (
  getToken: () => Promise<string | null>,
): Promise<CurrentLineItemReviewer> => {
  const response = await fetch(`${getCsapiBaseUrl()}/api/line-items/reviewers/current`, {
    method: 'POST',
    headers: await getBearerAuthHeaders(getToken),
  });
  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(getCurrentLineItemReviewerErrorMessage(responseBody));
  }

  const reviewer = responseBody?.data;
  if (!reviewer?.id) {
    throw new Error('Could not resolve current user as reviewer');
  }

  return reviewer;
};

export const duplicateLineItem = async (id: string | number, getToken: () => Promise<string | null>) => {
  const response = await fetch(`${getCsapiBaseUrl()}/api/line-items/${encodeURIComponent(String(id))}/duplicate`, {
    method: 'POST',
    headers: await getBearerAuthHeaders(getToken),
  });
  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(getDuplicateLineItemErrorMessage(responseBody));
  }

  const duplicatedLineItem = responseBody?.data;

  return {
    data: duplicatedLineItem,
    id: duplicatedLineItem?.id,
  };
};

export const createClicksCreative = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  variables: ClicksCreative,
) => {
  const { id, targetListIdsList, professionIdsList, specialtyIdsList, hideOnDaysList, __typename, ...clicksCreative } =
    variables;
  const clicksCreativeWithLineItemId = await withNativeLineItemIdForWrite(apolloClient, clicksCreative);
  const createMutationResult = await apolloClient.mutate<
    CreateClicksCreativeMutation,
    CreateClicksCreativeMutationVariables
  >({
    mutation: CreateClicksCreativeDocument,
    variables: {
      input: { clicksCreative: clicksCreativeWithLineItemId },
    },
  });

  const newClicksCreativeId = createMutationResult.data?.createClicksCreative?.clicksCreative?.id;

  if (newClicksCreativeId) {
    await apolloClient.mutate<SetClicksCreativeListsMutation, SetClicksCreativeListsMutationVariables>({
      mutation: SetClicksCreativeListsDocument,
      variables: {
        id: newClicksCreativeId,
        daysToHide: hideOnDaysList ?? [],
        targetListIds: targetListIdsList ?? [],
        professionIds: professionIdsList ?? [],
        specialtyIds: specialtyIdsList ?? [],
      },
    });
  }

  return {
    data: createMutationResult?.data?.createClicksCreative?.clicksCreative,
  };
};

export const updateClicksCreative = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  variables: ClicksCreative,
) => {
  const { id, targetListIdsList, professionIdsList, specialtyIdsList, hideOnDaysList, __typename, ...patch } =
    variables;
  // hasActivityData is generated from a function and is readonly
  delete patch.hasActivityData;
  const patchWithLineItemId = await withNativeLineItemIdForWrite(apolloClient, patch, {
    clearLineItemIdOnLookupMiss: true,
  });

  const targetingVariables =
    patchWithLineItemId.targetingType === ClicksCreativeTargetingType['TargetList']
      ? { targetListIds: targetListIdsList ?? [], professionIds: [], specialtyIds: [] }
      : {
          professionIds: professionIdsList ?? [],
          specialtyIds: specialtyIdsList ?? [],
          targetListIds: [],
        };

  const updateMutationResult = await apolloClient.mutate<
    UpdateClicksCreativeMutation,
    UpdateClicksCreativeMutationVariables
  >({
    mutation: UpdateClicksCreativeDocument,
    variables: {
      id,
      patch: patchWithLineItemId,
      daysToHide: hideOnDaysList ?? [],
      ...targetingVariables,
    },
  });

  return {
    data: {
      ...updateMutationResult?.data?.updateClicksCreative?.clicksCreative,
      placementsLineItemId: variables.placementsLineItemId,
    },
  };
};

export const archiveLabel = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  variables: ArchiveLabelMutationVariables,
) => {
  const { id, isArchived, name, updatedBy } = variables;

  const updateMutationResult = await apolloClient.mutate<ArchiveLabelMutation, ArchiveLabelMutationVariables>({
    mutation: ArchiveLabelDocument,
    variables: {
      id,
      isArchived,
      name,
      updatedBy,
    },
  });

  return {
    data: updateMutationResult?.data?.updateLabel?.label,
  };
};

export const createSponsoredContentTeaser = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  variables: SponsoredContentTeaser,
) => {
  const { id, targetListIdsList, professionIdsList, specialtyIdsList, __typename, ...sponsoredContentTeaser } =
    variables;
  const sponsoredContentTeaserWithLineItemId = await withNativeLineItemIdForWrite(apolloClient, sponsoredContentTeaser);
  const createMutationResult = await apolloClient.mutate<
    CreateSponsoredContentTeaserMutation,
    CreateSponsoredContentTeaserMutationVariables
  >({
    mutation: CreateSponsoredContentTeaserDocument,
    variables: {
      input: { sponsoredContentTeaser: sponsoredContentTeaserWithLineItemId },
    },
  });

  const newSponsoredContentTeaserId =
    createMutationResult.data?.createSponsoredContentTeaser?.sponsoredContentTeaser?.id;

  if (newSponsoredContentTeaserId) {
    await apolloClient.mutate({
      mutation: SetSponsoredContentTeaserListsDocument,
      variables: {
        id: newSponsoredContentTeaserId,
        targetListIds: targetListIdsList ?? [],
        professionIds: professionIdsList ?? [],
        specialtyIds: specialtyIdsList ?? [],
      },
    });
  }

  return {
    data: createMutationResult?.data?.createSponsoredContentTeaser?.sponsoredContentTeaser,
  };
};

export const updateSponsoredContentTeaser = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  variables: SponsoredContentTeaser,
) => {
  const { id, targetListIdsList, professionIdsList, specialtyIdsList, __typename, ...sponsoredContentTeaser } =
    variables;
  const sponsoredContentTeaserWithLineItemId = await withNativeLineItemIdForWrite(
    apolloClient,
    sponsoredContentTeaser,
    {
      clearLineItemIdOnLookupMiss: true,
    },
  );

  await apolloClient.mutate({
    mutation: UpdateSponsoredContentTeaserDocument,
    variables: {
      id,
      patch: sponsoredContentTeaserWithLineItemId,
      targetListIds: targetListIdsList ?? [],
      specialtyIds: specialtyIdsList ?? [],
      professionIds: professionIdsList ?? [],
    },
  });

  return {
    data: { id, ...sponsoredContentTeaserWithLineItemId },
  };
};

export const updateEndemicDriver = async (apolloClient: ApolloClient<NormalizedCacheObject>, variables: any) => {
  const { id, targetListIds, topicTagIds, specialtyIds, professionIds, targetingType, __typename, ...patch } =
    variables;

  // Update the main endemic driver record
  const updateMutationResult = await apolloClient.mutate<
    UpdateEndemicDriverMutation,
    UpdateEndemicDriverMutationVariables
  >({
    mutation: UpdateEndemicDriverDocument,
    variables: {
      input: {
        id,
        patch,
      },
    },
  });

  // Handle relationships based on targeting type — always clear unused targeting data
  if (targetingType === 'target_list') {
    await Promise.all([
      apolloClient.mutate<SetEndemicDriverTargetListsMutation, SetEndemicDriverTargetListsMutationVariables>({
        mutation: SetEndemicDriverTargetListsDocument,
        variables: { input: { endemicDriverId: id, targetListIds: targetListIds || [] } },
      }),
      apolloClient.mutate<SetEndemicDriverTopicTagsMutation, SetEndemicDriverTopicTagsMutationVariables>({
        mutation: SetEndemicDriverTopicTagsDocument,
        variables: { input: { endemicDriverId: id, topicTagIds: [] } },
      }),
      apolloClient.mutate<SetEndemicDriverSpecialtiesMutation, SetEndemicDriverSpecialtiesMutationVariables>({
        mutation: SetEndemicDriverSpecialtiesDocument,
        variables: { input: { endemicDriverId: id, specialtyIds: [] } },
      }),
      apolloClient.mutate<SetEndemicDriverProfessionsMutation, SetEndemicDriverProfessionsMutationVariables>({
        mutation: SetEndemicDriverProfessionsDocument,
        variables: { input: { endemicDriverId: id, professionIds: [] } },
      }),
    ]);
  } else if (targetingType === 'run_of_specialty') {
    await Promise.all([
      apolloClient.mutate<SetEndemicDriverSpecialtiesMutation, SetEndemicDriverSpecialtiesMutationVariables>({
        mutation: SetEndemicDriverSpecialtiesDocument,
        variables: { input: { endemicDriverId: id, specialtyIds: specialtyIds || [] } },
      }),
      apolloClient.mutate<SetEndemicDriverProfessionsMutation, SetEndemicDriverProfessionsMutationVariables>({
        mutation: SetEndemicDriverProfessionsDocument,
        variables: { input: { endemicDriverId: id, professionIds: professionIds || [] } },
      }),
      apolloClient.mutate<SetEndemicDriverTargetListsMutation, SetEndemicDriverTargetListsMutationVariables>({
        mutation: SetEndemicDriverTargetListsDocument,
        variables: { input: { endemicDriverId: id, targetListIds: [] } },
      }),
      apolloClient.mutate<SetEndemicDriverTopicTagsMutation, SetEndemicDriverTopicTagsMutationVariables>({
        mutation: SetEndemicDriverTopicTagsDocument,
        variables: { input: { endemicDriverId: id, topicTagIds: [] } },
      }),
    ]);
  } else if (targetingType === 'topic_tag') {
    await Promise.all([
      apolloClient.mutate<SetEndemicDriverTopicTagsMutation, SetEndemicDriverTopicTagsMutationVariables>({
        mutation: SetEndemicDriverTopicTagsDocument,
        variables: { input: { endemicDriverId: id, topicTagIds: topicTagIds || [] } },
      }),
      apolloClient.mutate<SetEndemicDriverTargetListsMutation, SetEndemicDriverTargetListsMutationVariables>({
        mutation: SetEndemicDriverTargetListsDocument,
        variables: { input: { endemicDriverId: id, targetListIds: [] } },
      }),
      apolloClient.mutate<SetEndemicDriverSpecialtiesMutation, SetEndemicDriverSpecialtiesMutationVariables>({
        mutation: SetEndemicDriverSpecialtiesDocument,
        variables: { input: { endemicDriverId: id, specialtyIds: [] } },
      }),
      apolloClient.mutate<SetEndemicDriverProfessionsMutation, SetEndemicDriverProfessionsMutationVariables>({
        mutation: SetEndemicDriverProfessionsDocument,
        variables: { input: { endemicDriverId: id, professionIds: [] } },
      }),
    ]);
  }

  return {
    data: updateMutationResult?.data?.updateEndemicDriver?.endemicDriver,
  };
};

export const createEndemicDriver = async (apolloClient: ApolloClient<NormalizedCacheObject>, variables: any) => {
  const { targetListIds, topicTagIds, specialtyIds, professionIds, targetingType, __typename, ...endemicDriver } =
    variables;

  // Create the main endemic driver record
  const createMutationResult = await apolloClient.mutate<
    CreateEndemicDriverMutation,
    CreateEndemicDriverMutationVariables
  >({
    mutation: CreateEndemicDriverDocument,
    variables: {
      input: {
        endemicDriver,
      },
    },
  });

  const newEndemicDriverId = createMutationResult.data?.createEndemicDriver?.endemicDriver?.id;

  if (newEndemicDriverId) {
    if (targetingType === 'target_list') {
      await apolloClient.mutate<SetEndemicDriverTargetListsMutation, SetEndemicDriverTargetListsMutationVariables>({
        mutation: SetEndemicDriverTargetListsDocument,
        variables: { input: { endemicDriverId: newEndemicDriverId, targetListIds: targetListIds || [] } },
      });
    } else if (targetingType === 'run_of_specialty') {
      await Promise.all([
        apolloClient.mutate<SetEndemicDriverSpecialtiesMutation, SetEndemicDriverSpecialtiesMutationVariables>({
          mutation: SetEndemicDriverSpecialtiesDocument,
          variables: { input: { endemicDriverId: newEndemicDriverId, specialtyIds: specialtyIds || [] } },
        }),
        apolloClient.mutate<SetEndemicDriverProfessionsMutation, SetEndemicDriverProfessionsMutationVariables>({
          mutation: SetEndemicDriverProfessionsDocument,
          variables: { input: { endemicDriverId: newEndemicDriverId, professionIds: professionIds || [] } },
        }),
      ]);
    } else if (targetingType === 'topic_tag') {
      await apolloClient.mutate<SetEndemicDriverTopicTagsMutation, SetEndemicDriverTopicTagsMutationVariables>({
        mutation: SetEndemicDriverTopicTagsDocument,
        variables: { input: { endemicDriverId: newEndemicDriverId, topicTagIds: topicTagIds || [] } },
      });
    }
  }

  return {
    data: createMutationResult?.data?.createEndemicDriver?.endemicDriver,
  };
};

export const createLeadGenCampaign = async (apolloClient: ApolloClient<NormalizedCacheObject>, variables: any) => {
  const { targetListIds, __typename, ...leadGenCampaign } = variables;
  const createMutationResult = await apolloClient.mutate<
    CreateLeadGenCampaignMutation,
    CreateLeadGenCampaignMutationVariables
  >({
    mutation: CreateLeadGenCampaignDocument,
    variables: leadGenCampaign,
  });

  const newLeadGenCampaignId = createMutationResult.data?.createLeadGenCampaign?.leadGenCampaign?.id;

  if (newLeadGenCampaignId && targetListIds && targetListIds.length > 0) {
    await apolloClient.mutate({
      mutation: SetLeadGenCampaignTargetListsDocument,
      variables: {
        id: newLeadGenCampaignId,
        targetListIds: targetListIds,
      },
    });
  }

  return {
    data: createMutationResult?.data?.createLeadGenCampaign?.leadGenCampaign,
  };
};

export const updateLeadGenCampaign = async (apolloClient: ApolloClient<NormalizedCacheObject>, variables: any) => {
  const { id, targetListIds, __typename, ...patch } = variables;

  const updateMutationResult = await apolloClient.mutate<
    UpdateLeadGenCampaignMutation,
    UpdateLeadGenCampaignMutationVariables
  >({
    mutation: UpdateLeadGenCampaignDocument,
    variables: {
      id,
      ...patch,
      targetListIds: targetListIds ?? [],
    },
  });

  return {
    data: updateMutationResult?.data?.updateLeadGenCampaign?.leadGenCampaign,
  };
};
